import { Main_Plain } from "monica-alexandria";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const SalesClockBar = styled.div`
    position: absolute;
    bottom: -3rem;
    left: 0;
    width: 100%;
    display: flex;
    gap: 1rem;
    align-items: center;
    z-index: 4;
`;

const SalesClockBarLine = styled.div`
    width: 100%;
    height: 0.6rem;
    overflow: hidden;
    border-radius: 1rem;
    background: ${(p) => p.theme.low};
    box-shadow: ${(p) => p.theme.in};
`;

const SalesClockBarLineInner = styled.div`
    width: ${(p) => `${p.percent}%`};
    height: 100%;
    border-radius: 1rem;
    background: ${(p) => p.attention ? "var(--warning)" : "var(--main)"};
    transition: width 1s linear, background 0.3s ease;
`;

const read_clock_ticking = (clock = {}) => {
    return Boolean(clock?.ticking || clock?.clock?.ticking);
};

const read_clock_started = (clock = {}) => {
    return Boolean(clock?.started || clock?.firstStartedAt || clock?.startedAt);
};

// Reads live seconds from the last HR snapshot so display keeps moving between syncs.
const read_live_remaining_seconds = (clock = {}, renderTime = Date.now()) => {
    const remainingSeconds = Number(clock?.remainingSeconds ?? 0);

    if (!read_clock_ticking(clock)) {
        return Math.max(remainingSeconds, 0);
    }

    const snapshotTime = new Date(clock?.snapshotAt || clock?.startedAt || renderTime).getTime();
    const elapsedSeconds = Number.isNaN(snapshotTime)
        ? 0
        : Math.max(Math.floor((renderTime - snapshotTime) / 1000), 0);

    return Math.max(remainingSeconds - elapsedSeconds, 0);
};

// Converts the HR snapshot into a remaining-width ratio for the board line.
const read_clock_remaining_percent = (clock = {}, remainingSeconds = 0) => {
    const totalSeconds = Number(clock?.totalSeconds || 0);
    const rawPercent = totalSeconds ? (remainingSeconds / totalSeconds) * 100 : 0;

    return Math.max(0, Math.min(rawPercent, 100));
};

const read_clock_status_text = (remainingSeconds = 0) => {
    const hours = `${Math.floor(remainingSeconds / 3600)}`.padStart(2, "0");
    const minutes = `${Math.floor((remainingSeconds % 3600) / 60)}`.padStart(2, "0");

    return `${hours}:${minutes} left`;
};

// Shows the live HR countdown and lets the current Sales member pause or resume it.
export const Sales_Clock_Bar = (props) => {
    const { t } = useTranslation();
    const [renderTime, setRenderTime] = React.useState(Date.now());
    const clock = props.clock || {};
    const ticking = read_clock_ticking(clock);
    const remainingSeconds = read_live_remaining_seconds(clock, renderTime);
    const remainingPercent = read_clock_remaining_percent(clock, remainingSeconds);
    const hideClockButton = !read_clock_started(clock) && remainingSeconds >= 8 * 60 * 60;
    const buttonText = props.loading ? t("Loading") : ticking ? t("Pause") : t("Continue");
    const buttonIcon = ticking ? "Pause" : "Play";
    const lineTitle = `${read_clock_status_text(remainingSeconds)} | ${t(ticking ? "HR running" : "HR paused")}`;

    React.useEffect(() => {
        const interval = window.setInterval(() => {
            setRenderTime(Date.now());
        }, 1000);

        return () => {
            window.clearInterval(interval);
        };
    }, []);

    return (
        <SalesClockBar attention={props.attention}>
            {!hideClockButton ? (
                <Main_Plain size="small" text={buttonText} iconLeft={buttonIcon} disabled={props.loading} onClick={props.onToggleClock} />
            ) : null}
            <SalesClockBarLine title={lineTitle}>
                <SalesClockBarLineInner percent={remainingPercent} attention={props.attention} />
            </SalesClockBarLine>
        </SalesClockBar>
    );
};
